import type { SavedQuery } from "@api/generated/model";
import { useQuerySaved } from "@api/generated/query/query";
import { hint } from "@i18n";
import { Select, Stack } from "@mantine/core";
import { Link } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

type Group = { group: string; items: { value: string; label: string }[] };

/** The user's own saved queries first, then the ones others share (with who shared them), as Select groups; a group
 *  without queries is left out. */
export function savedQueryOptions(saved: SavedQuery[], t: TFunction): Group[] {
  const item = (q: SavedQuery) => ({
    value: String(q.id),
    label: q.mine ? q.name : `${q.name} · ${t("queries.sharedBy", { name: q.owner })}`,
  });
  const groups = [
    { group: t("queries.mine"), items: saved.filter((q) => q.mine).map(item) },
    { group: t("queries.sharedWithMe"), items: saved.filter((q) => !q.mine).map(item) },
  ];
  return groups.filter((g) => g.items.length > 0);
}

/** The chosen saved query of a window and its stamp (``updated_at`` as text) for a preview's query key: saving the
 *  query again in the builder reads the preview again. ``ready`` is false while the saved list is still loading for a
 *  chosen query, so a preview waits for the stamp instead of asking twice; with ``enabled`` false (the window's source
 *  is not a query) the list is not read and there is no query. */
export function useSavedQuery(id: number | null | undefined, enabled = true) {
  const on = enabled && id != null;
  const saved = useQuerySaved({ query: { staleTime: 30_000, enabled: on } });
  const query = on ? (saved.data?.find((q) => q.id === id) ?? null) : null;
  return { query, stamp: query ? String(query.updated_at ?? "") : "", ready: !on || !saved.isPending };
}

/** A saved query as the input of a window (the legacy "close the Query on this window"): the user's own and shared
 *  saved queries, and a link that opens the chosen one in the query builder (or starts a new one). The server reads
 *  the query's rows, in its order, through ``shared.query_source`` (docs/query-source.md); ``useSavedQuery`` gives
 *  the chosen query's stamp for a preview's query key. */
export function SavedQuerySelect(props: {
  value: number | null;
  onChange: (id: number | null) => void;
  label?: string;
  description?: string;
  size?: "xs" | "sm";
  w?: number | string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
}) {
  const { t } = useTranslation();
  const saved = useQuerySaved({ query: { staleTime: 30_000 } });
  const queries = useMemo(() => saved.data ?? [], [saved.data]);
  const data = useMemo(() => savedQueryOptions(queries, t), [queries, t]);
  const chosen = queries.find((q) => q.id === props.value) ?? null;
  const empty = saved.isSuccess && queries.length === 0;
  return (
    <Stack gap={4} align="flex-start">
      <Select
        size={props.size ?? "xs"}
        w={props.w}
        label={props.label ?? t("queries.source.label")}
        description={props.description ?? chosen?.description ?? undefined}
        placeholder={empty ? t("queries.source.empty") : hint("searchSelect")}
        withAsterisk={props.required}
        disabled={props.disabled || empty}
        error={props.error}
        searchable
        clearable={!props.required}
        nothingFoundMessage={hint("noResults")}
        data={data}
        value={chosen ? String(chosen.id) : null}
        onChange={(v) => props.onChange(queries.find((x) => String(x.id) === v)?.id ?? null)}
      />
      {chosen ? (
        <Link className="sm" to="/trials/queries" search={{ q: chosen.id }}>
          {t("queries.source.openInEditor")}
        </Link>
      ) : (
        <Link className="sm" to="/trials/queries">
          {t("queries.source.newQuery")}
        </Link>
      )}
    </Stack>
  );
}
